import type { PostWithTargets } from '@postpilot/core/domain';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { motion } from 'framer-motion';
import {
  CalendarClock,
  ExternalLink,
  MoreHorizontal,
  Pencil,
  RotateCw,
  Trash2,
} from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/primitives';
import { EmptyState, ErrorState, TableRowSkeleton } from '@/components/ui/states';
import { StatusBadge } from '@/components/ui/status-badge';
import { formatRelative, formatScheduledAt } from '@/lib/dayjs';
import { cn } from '@/lib/utils';
import { selectHasActiveFilters, usePostsStore } from '@/stores/posts-store';

const columnHelper = createColumnHelper<PostWithTargets>();

export function QueueTable({ onEdit }: { onEdit: (post: PostWithTargets) => void }) {
  const posts = usePostsStore((s) => s.posts);
  const loading = usePostsStore((s) => s.loading);
  const refetching = usePostsStore((s) => s.refetching);
  const error = usePostsStore((s) => s.error);
  const fetch = usePostsStore((s) => s.fetch);
  const remove = usePostsStore((s) => s.remove);
  const retry = usePostsStore((s) => s.retry);
  const hasActiveFilters = usePostsStore(selectHasActiveFilters);

  const [confirmDelete, setConfirmDelete] = React.useState<PostWithTargets | null>(null);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('content', {
        header: 'Post',
        cell: (info) => {
          const post = info.row.original;
          const text = info.getValue();

          return (
            <div className="min-w-0 max-w-md">
              <p className={cn('truncate text-sm', !text && 'text-muted-foreground italic')}>
                {text || 'Empty draft'}
              </p>
              {post.media.length > 0 && (
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {post.media.length} image{post.media.length === 1 ? '' : 's'}
                </p>
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const post = info.row.original;
          const failed = post.targets.filter((t) => t.status === 'failed');

          return (
            <div className="flex items-center gap-1.5">
              <StatusBadge status={info.getValue()} />

              {/* The error is the single most useful thing on a failed row, and
                  it is too long to show inline. A hover reveals it without
                  making the user open a dialog to find out what went wrong. */}
              {failed.length > 0 && failed[0]?.errorMessage && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-muted-foreground text-xs underline decoration-dotted underline-offset-2">
                      why?
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">{failed[0].errorMessage}</TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      }),

      columnHelper.display({
        id: 'accounts',
        header: 'Accounts',
        cell: (info) => {
          const targets = info.row.original.targets;

          return (
            <div className="flex -space-x-1.5">
              {targets.slice(0, 4).map((target) => (
                <Tooltip key={target.id}>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Avatar className="border-background size-7 border-2">
                        <AvatarImage src={target.account?.avatarUrl ?? undefined} alt="" />
                        <AvatarFallback className="text-[9px]">
                          {target.account?.username.slice(0, 2).toUpperCase() ?? '??'}
                        </AvatarFallback>
                      </Avatar>

                      {/* A per-account status dot. This is the payoff of the
                          fan-out model: at a glance you can see the post landed
                          on three accounts and failed on one. */}
                      <span
                        className={cn(
                          'border-background absolute -bottom-0.5 -right-0.5 size-2 rounded-full border',
                          target.status === 'published' && 'bg-status-published',
                          target.status === 'failed' && 'bg-status-failed',
                          target.status === 'processing' && 'bg-status-processing',
                          target.status === 'pending' && 'bg-status-scheduled',
                          target.status === 'cancelled' && 'bg-status-draft',
                        )}
                        aria-hidden
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    @{target.account?.username ?? 'unknown'}
                    {target.errorMessage && ` — ${target.errorMessage}`}
                  </TooltipContent>
                </Tooltip>
              ))}

              {targets.length > 4 && (
                <div className="border-background bg-muted flex size-7 items-center justify-center rounded-full border-2 text-[10px] font-medium">
                  +{targets.length - 4}
                </div>
              )}

              {targets.length === 0 && <span className="text-muted-foreground text-xs">—</span>}
            </div>
          );
        },
      }),

      columnHelper.accessor('scheduledAt', {
        header: 'When',
        cell: (info) => {
          const post = info.row.original;
          const when = post.postedAt ?? info.getValue();

          if (!when) return <span className="text-muted-foreground text-xs">—</span>;

          return (
            <div className="whitespace-nowrap">
              <div className="text-sm">{formatScheduledAt(when)}</div>
              <div className="text-muted-foreground text-xs">{formatRelative(when)}</div>
            </div>
          );
        },
      }),

      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const post = info.row.original;
          const published = post.targets.filter((t) => t.providerPostUrl);
          const canEdit = post.status !== 'processing' && post.status !== 'published';
          const canRetry = post.status === 'failed' || post.status === 'partial';

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Post actions">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-48">
                {canEdit && (
                  <DropdownMenuItem onSelect={() => onEdit(post)}>
                    <Pencil />
                    Edit
                  </DropdownMenuItem>
                )}

                {canRetry && (
                  <DropdownMenuItem onSelect={() => void retry(post.id)}>
                    <RotateCw />
                    Retry failed
                  </DropdownMenuItem>
                )}

                {published.length > 0 && (
                  <DropdownMenuItem
                    onSelect={() => window.open(published[0]!.providerPostUrl!, '_blank')}
                  >
                    <ExternalLink />
                    View on X
                  </DropdownMenuItem>
                )}

                {(canEdit || canRetry || published.length > 0) && <DropdownMenuSeparator />}

                <DropdownMenuItem destructive onSelect={() => setConfirmDelete(post)}>
                  <Trash2 />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      }),
    ],
    [onEdit, retry],
  );

  const table = useReactTable({
    data: posts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Sorting, filtering and pagination all happen on the SERVER (see the API's
    // queue route). Enabling TanStack's client-side models here would sort only
    // the current page, which looks correct until page 2 and is then subtly wrong.
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
  });

  if (loading) {
    return (
      <div className="bg-card rounded-xl border">
        <TableRowSkeleton rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl border">
        <ErrorState message={error} onRetry={() => void fetch()} />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="bg-card rounded-xl border">
        {hasActiveFilters ? (
          <EmptyState
            icon={CalendarClock}
            title="No posts match these filters"
            description="Try widening your search, or clear the filters to see everything in the queue."
            action={{
              label: 'Clear filters',
              onClick: () => usePostsStore.getState().resetFilters(),
            }}
          />
        ) : (
          <EmptyState
            icon={CalendarClock}
            title="Your queue is empty"
            description="Schedule a post and it will show up here, along with its live publishing status for every account."
            action={{ label: 'Compose a post', onClick: () => (window.location.href = '/compose') }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'bg-card overflow-hidden rounded-xl border transition-opacity',
          // Dim rather than unmount during a refetch. Swapping a populated table
          // for a skeleton on every filter change is far more disorienting.
          refetching && 'opacity-60',
        )}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30 border-b">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody className="divide-border divide-y">
              {table.getRowModel().rows.map((row, index) => (
                <motion.tr
                  key={row.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  // A tiny stagger, capped. Uncapped, row 40 would take 1.2s to
                  // appear and the table would feel like it was loading forever.
                  transition={{ duration: 0.18, delay: Math.min(index * 0.02, 0.2) }}
                  className="hover:bg-muted/40 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Delete this post?"
        // Concrete about the one consequence a user would not think of.
        description={
          confirmDelete?.targets.some((t) => t.status === 'published')
            ? 'This removes the post from your queue and history here. It does NOT delete anything already published on X — you would need to delete that on X itself.'
            : 'This removes the post from your queue. It has not been published, so nothing will appear on X.'
        }
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (confirmDelete) await remove(confirmDelete.id);
        }}
      />
    </>
  );
}
